import { isAcceptedCurrency, type ReceivableInvoiceLineItemRevenue, useApiDealsV1RevenueList } from 'api';
import { type I18nTranslateFunction, useTranslation } from 'i18n';
import { addError } from 'monitor';
import { useParams } from 'react-router-dom';
import {
  BorderedLinearProgress,
  Chip,
  CircularProgress,
  DataGrid,
  formatCurrency,
  formatNumber,
  type GridColDef,
  Link,
  NoData,
  Page,
  Typography,
} from 'ui';
import { ArrowCircleLeft } from 'ui/icons';

import { InvoiceStatus } from '../../../invoice/enums';
import { getColorForInvoiceStatus } from '../getColorForInvoiceStatus';

const getColumns = (t: I18nTranslateFunction<string>): GridColDef<ReceivableInvoiceLineItemRevenue>[] => [
  {
    field: 'description',
    headerName: t('dealHub.dealRevenueDetails.columns.description'),
    width: 550,
  },
  { field: 'id', headerName: t('dealHub.dealRevenueDetails.columns.id') },
  { field: 'quantity', headerName: t('dealHub.dealRevenueDetails.columns.quantity') },
  { field: 'unit_amount', headerName: t('dealHub.dealRevenueDetails.columns.unitAmount') },
  {
    field: 'line_total',
    headerName: t('dealHub.dealRevenueDetails.columns.lineTotal'),
    valueGetter: (_value, row) => {
      if (row.quantity && row.unit_amount) {
        return Number(row.quantity) * Number(row.unit_amount);
      }
    },
  },
  {
    field: 'invoice_status',
    headerName: t('dealHub.dealRevenueDetails.columns.invoiceStatus'),
    renderCell: (params) => {
      const statusColor = getColorForInvoiceStatus(params.value);
      return <Chip label={params.value} color={statusColor} />;
    },
    width: 150,
  },
  { field: 'invoice_xero_id', headerName: t('dealHub.dealRevenueDetails.columns.invoiceXeroId') },
  {
    field: 'invoice_xero_amount_paid',
    headerName: t('dealHub.dealRevenueDetails.columns.invoiceXeroAmountPaid'),
  },
  {
    field: 'invoice_xero_fully_paid_on',
    headerName: t('dealHub.dealRevenueDetails.columns.invoiceXeroFullyPaidOn'),
  },
];

const getSumOfLineTotal = (data: ReceivableInvoiceLineItemRevenue[]) => {
  // sum of each Invoice Line Item total
  return data.reduce((acc, item) => acc + (Number(item.quantity) || 0) * (Number(item.unit_amount) || 0), 0);
};

const getSumOfAmountPaid = (data: ReceivableInvoiceLineItemRevenue[]) => {
  // filter line items by PAID status and sum the amount paid
  return data
    .filter((item) => item.invoice_status === InvoiceStatus.PAID)
    .reduce((acc, item) => acc + (Number(item.quantity) || 0) * (Number(item.unit_amount) || 0), 0);
};

const getPercentagePaid = (linesTotal: number, amountPaid: number) => {
  return (amountPaid / linesTotal) * 100;
};

export const DealRevenueDetails = () => {
  const { t } = useTranslation('orders');
  const { deal_id } = useParams();
  const columns = getColumns(t);

  const { data, isFetching } = useApiDealsV1RevenueList(Number(deal_id), {
    query: {
      enabled: Boolean(deal_id),
    },
  });

  if (isFetching) {
    return <CircularProgress />;
  }

  if (!data || data.length === 0) {
    addError(Error('No data found for deal'), { deal_id });
    return (
      <Page>
        <NoData>{t('dealHub.dealRevenueDetails.noRevenueDataForDeal')}</NoData>
      </Page>
    );
  }

  const totalAmountPaid = getSumOfAmountPaid(data);
  const linesTotal = getSumOfLineTotal(data);
  const percentagePaid = getPercentagePaid(linesTotal, totalAmountPaid);

  // all line items have the same currency code
  const currencyCode = data.length ? data[0].invoice_currency_code : '';

  const currency = isAcceptedCurrency(currencyCode) ? currencyCode : '';

  return (
    <Page>
      <Link href={`/post-deal/dealhub/deal/details/${deal_id}/`}>
        <ArrowCircleLeft />
        {t('dealHub.dealRevenueDetails.actions.backToDeal', { values: { dealId: deal_id } })}
      </Link>
      <Typography variant="h4">
        {t('dealHub.dealRevenueDetails.summary', {
          values: {
            paid: formatCurrency(totalAmountPaid, {
              currencyCode: currency,
            }),
            percentage: formatNumber(percentagePaid, {
              minimumFractionDigits: 2,
              style: 'percent',
            }),
            total: formatCurrency(linesTotal, {
              currencyCode: currency,
            }),
          },
        })}
      </Typography>
      <BorderedLinearProgress variant="determinate" value={percentagePaid} />
      <DataGrid rows={data} columns={columns} hideFooter sx={{ overflowX: 'scroll' }} />
    </Page>
  );
};
